import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import Switch from '../../components/common/form/switch';
import StackedHorizontalBarChart from '../../components/topics/charts/stackedHorizontalBarChart';
import ScalableTimeChart from '../../components/topics/charts/scalableTimeChart';
import Card from '../../components/common/card';
import VotesMap from '../../components/votes/votesMap';
import {
    VOTE_CHART_MODES,
    getGenderGroupData,
    getAgeGroupData,
    getLocationGroupData,
    getSourceStackData,
    getSourceGroupData
} from '../../helpers/voteDataHelper';
import memoizeOne from 'memoize-one';
import { initChartSizeConfig, updateChartWidth } from '../../helpers/topicsHelper';
import { STACKING_OPTIONS } from '../../helpers/graphsService';
import './css/topicDetailsGraphs.css';
import FeaturesContext from "../contexts/featuresContext";
import { ENTITY_FEATURES } from "hollerlive-shared/constants";
import { COMPARATORS } from "../../helpers/featuresHelper";
import NoPermissionLabel from '../../components/common/noPermissionLabel';
import messages from '../../helpers/messages';

export class TopicDetailsGraphs extends Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            maxChartWidth: null,
            halfChartWidth: null,
            shouldUpdateChartsWidth: false,
            allLocationsShow: false,
            votesMapShow: true,
            votesByGenderChartMode: VOTE_CHART_MODES.STACK,
            votesByAgeChartMode: VOTE_CHART_MODES.STACK,
            votesByLocationChartMode: VOTE_CHART_MODES.STACK,
            votesBySourceChartMode: VOTE_CHART_MODES.STACK,
            shouldForceResize: false,
            timeScaleLabel: null
        };

        this.handleResize = this.handleResize.bind(this);
        this.handleVotesByTimeTimeScaleValueChange = this.handleVotesByTimeTimeScaleValueChange.bind(this);
        this.toggleVotesBy = this.toggleVotesBy.bind(this);
        this.getVotesDataChunkViews = this.getVotesDataChunkViews.bind(this);
        this.chartsSizesConfiguration = null;
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);        
        this.chartsSizesConfiguration = initChartSizeConfig(this.chartsContainerDom, 'card');
        
        const { halfChartWidth, maxChartWidth } = this.chartsSizesConfiguration;

        this.setState({ halfChartWidth, maxChartWidth });
    }
    
    componentDidUpdate(prevProps, prevState) {
        const { dateFrom, dateTo, voterFilters } = this.props;

        if (this.state.shouldUpdateChartsWidth) {
            this.setChartsWidth();
        }

        if ((prevProps.dateFrom !== dateFrom) || (prevProps.dateTo !== dateTo) ||
          !isEqual(prevProps.voterFilters, voterFilters)) {
            this.setState({ shouldForceResize: true, timeScaleLabel: null });
        } else if (prevState.shouldForceResize) {
            this.setState({ shouldForceResize: false });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize() {
        this.setState({
            maxChartWidth: null,
            halfChartWidth: null,
            shouldUpdateChartsWidth: true
        });
    }

    handleVotesByTimeTimeScaleValueChange(selectedTimeScaleName) {
        this.setState({ timeScaleLabel: selectedTimeScaleName });
    }

    setChartsWidth(){
        updateChartWidth(this.chartsSizesConfiguration);
        const { halfChartWidth, maxChartWidth } = this.chartsSizesConfiguration;

        this.setState({ halfChartWidth, maxChartWidth, shouldUpdateChartsWidth: false });
    }
    
    toggleShowAllLocations(ev){
        if(ev){
            ev.preventDefault();
        }
        this.setState({ allLocationsShow: !this.state.allLocationsShow });
    }

    toggleShowVotesMap(ev){
        if(ev){
            ev.preventDefault();
        }
        this.setState({ votesMapShow: !this.state.votesMapShow });
    }
    
    toggleVotesBy(chartModeKey) {
        if(this.state[chartModeKey] === VOTE_CHART_MODES.STACK) {
            this.setState({[chartModeKey]: VOTE_CHART_MODES.MEAN});
        } else {
            this.setState({[chartModeKey]: VOTE_CHART_MODES.STACK});
        }
    }

    getVotesDataChunkViews(filteredVotesForPeriod) {
        return {
			votesCombinedByGenderAndSorted:
				filteredVotesForPeriod.length > 0 ? this._getVotesByGender(filteredVotesForPeriod) : [],
			votesCombinedByAgeAndSorted:
				filteredVotesForPeriod.length > 0 ? this._getVotesByAge(filteredVotesForPeriod) : [],
			votesCombinedByCityAndSorted:
				filteredVotesForPeriod.length > 0 ? this._getVotesByCity(filteredVotesForPeriod) : [],
			votesWithSources:
				filteredVotesForPeriod.length > 0 ? this._getVotesWithSources(filteredVotesForPeriod) : [],
			votesCombinedBySourceAndSorted:
				filteredVotesForPeriod.length > 0 ? this._getVotesBySource(filteredVotesForPeriod) : []
		};
    }

    _getVotesByGender = memoizeOne(rawVotes =>
        getGenderGroupData(rawVotes).sort((a, b) => b.votesCount - a.votesCount)
    );

    _getVotesByAge = memoizeOne(rawVotes =>
        getAgeGroupData(rawVotes).sort((a, b) => b.votesCount - a.votesCount)
    );

    _getVotesByCity = memoizeOne(rawVotes =>
        getLocationGroupData(rawVotes).sort((a, b) => b.votesCount - a.votesCount)
    );
    
    _getVotesWithSources = memoizeOne(rawVotes => getSourceStackData(rawVotes));

    _getVotesBySource = memoizeOne(rawVotes =>
        getSourceGroupData(rawVotes).sort((a, b) => b.votesCount - a.votesCount)
    );
    
    render() {
        const filteredVotesForPeriod = this.props.votes;
		const {
			votesCombinedByGenderAndSorted,
			votesCombinedByAgeAndSorted,
			votesCombinedByCityAndSorted,
			votesWithSources,
			votesCombinedBySourceAndSorted
		} = this.getVotesDataChunkViews(filteredVotesForPeriod);
        
        const chartMaxWidth = this.state.maxChartWidth;
        const chartHalfWidth = this.state.halfChartWidth;
        const chartLocationHeight = this.state.allLocationsShow ? votesCombinedByCityAndSorted.length * 30 : 300;
        const chartSourceHeight = Math.max(votesCombinedBySourceAndSorted.length * 30, 300);
        
        return (
            <div className="topic-details-graphs">
                <div id="vis-scatter" />
                <div id="vis-tooltip" className="vg-tooltip" />
                
                <div className="charts-container row-fluid clearfix export-pdf-votes-analysis-container" ref={chartsContainer => this.chartsContainerDom = chartsContainer}>
                    <div className="col-md-12 col-xs-12">
                        <Card title="Votes by time" hoffset={false} className="pdf-export-votes-graph-wrapper">
                            <ScalableTimeChart
                                type="line"
                                data={filteredVotesForPeriod}
                                dataFieldName="vote"
                                chartYAxisTitle="Votes mean"
                                chartXAxisTitle="Days"
                                className="export-pdf-chart-wrapper"
                                showLegend={true}
                                width={chartMaxWidth}
                                dateFrom={this.props.dateFrom} 
                                dateTo={this.props.dateTo}
                                onTimeScaleChange={this.handleVotesByTimeTimeScaleValueChange}
                                showScaleSelector={true}
                            />
                        </Card>
                    </div>

                    <div className="col-md-12 col-xs-12">
                        <Card title="Votes by volume" hoffset={false} className="pdf-export-votes-graph-wrapper">
                            <ScalableTimeChart
                                type="bar"
                                data={filteredVotesForPeriod}
                                dataFieldName="vote"
                                stackFieldOptions={[STACKING_OPTIONS.sentiment, STACKING_OPTIONS.ageGroups, STACKING_OPTIONS.gender]}
                                chartYAxisTitle="Total votes"
                                chartXAxisTitle="Days"
                                className="export-pdf-chart-wrapper"
                                showLegend={true}
                                width={chartMaxWidth}
                                dateFrom={this.props.dateFrom} 
                                dateTo={this.props.dateTo}
                                timeScaleLabel={this.state.timeScaleLabel}
                            />
                        </Card>
                    </div>
                    
                    <div className="col-md-6 col-xs-12">
                        <Card title="Votes by gender" hoffset={false} className="pdf-export-votes-graph-wrapper">
                            <div className='chart-control-options export-pdf-chart-control-options'>
                                <span>showing as&nbsp;</span>
                                <Switch
                                    value={this.state.votesByGenderChartMode === VOTE_CHART_MODES.STACK}
                                    onChange={() => this.toggleVotesBy('votesByGenderChartMode')}
                                    offLabel="mean"
                                    onLabel="stack" />
                            </div>
                            {this.state.votesByGenderChartMode === VOTE_CHART_MODES.STACK ?
                                <StackedHorizontalBarChart
                                    key='votesByGenderChartStack'
                                    data={filteredVotesForPeriod}
                                    dataFieldName="gender"
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment, STACKING_OPTIONS.ageGroups]}
                                    chartYAxisTitle="Gender"
                                    chartXAxisTitle="Votes Count"
                                    className="export-pdf-chart-wrapper"
                                    showLegend={true}
                                    width={chartHalfWidth} /> :
                                <StackedHorizontalBarChart
                                    key='votesByGenderChartMean'
                                    data={votesCombinedByGenderAndSorted}
                                    dataFieldName='gender'
                                    aggregationFieldName='votesCount'
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment]}
                                    chartYAxisTitle="Gender"
                                    chartXAxisTitle="Votes Count"
                                    className='export-pdf-chart-wrapper'
                                    showLegend={true}
                                    width={chartHalfWidth}
                                    forceResize={this.state.shouldForceResize} />}
                            
                        </Card>
                    </div>
                    <div className="col-md-6 col-xs-12">
                        <Card title="Votes by age" hoffset={false} className="pdf-export-votes-graph-wrapper">
                            <div className='chart-control-options export-pdf-chart-control-options'>
                                <span>showing as&nbsp;</span>
                                <Switch
                                    value={this.state.votesByAgeChartMode === VOTE_CHART_MODES.STACK}
                                    onChange={() => this.toggleVotesBy('votesByAgeChartMode')}
                                    offLabel="mean"
                                    onLabel="stack" />
                            </div>
                            {this.state.votesByAgeChartMode === VOTE_CHART_MODES.STACK ?
                                <StackedHorizontalBarChart
                                    key='votesByAgeChartStack'
                                    data={filteredVotesForPeriod}
                                    dataFieldName="ageGroup"
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment, STACKING_OPTIONS.gender]}
                                    chartYAxisTitle="Age group"
                                    chartXAxisTitle="Votes Count"
                                    className="export-pdf-chart-wrapper"
                                    showLegend={true}
                                    width={chartHalfWidth} /> :
                                <StackedHorizontalBarChart
                                    key='votesByAgeChartMean'
                                    data={votesCombinedByAgeAndSorted}
                                    dataFieldName='ageGroup'
                                    aggregationFieldName='votesCount'
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment]}
                                    chartYAxisTitle="Age group"
                                    chartXAxisTitle="Votes Count"
                                    className='export-pdf-chart-wrapper'
                                    showLegend={true}
                                    width={chartHalfWidth}
                                    forceResize={this.state.shouldForceResize} />}
                        </Card>
                    </div>
    
                    { this.state.votesMapShow &&
                    <div className="col-md-12 col-xs-12">
                        <Card title="Votes map" hoffset={ false }>
                            <VotesMap data={ votesCombinedByCityAndSorted }/>
                        </Card>
                    </div>
                    }
                    
                    <div className="col-md-12 col-xs-12">
                        <Card title="Votes by location" hoffset={false} className="pdf-export-votes-graph-wrapper">
                            <div className='chart-control-options export-pdf-chart-control-options'>
                                <span>showing as&nbsp;</span>
                                <Switch
                                    value={this.state.votesByLocationChartMode === VOTE_CHART_MODES.STACK}
                                    onChange={() => this.toggleVotesBy('votesByLocationChartMode')}
                                    offLabel="mean"
                                    onLabel="stack" />
                            </div>
                            {this.state.votesByLocationChartMode === VOTE_CHART_MODES.STACK ?
                                <StackedHorizontalBarChart
                                    key='votesByLocationChartStack'
                                    data={filteredVotesForPeriod}
                                    dataFieldName='city'
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment, STACKING_OPTIONS.gender, STACKING_OPTIONS.ageGroups]}
                                    chartYAxisTitle="Locations"
                                    chartXAxisTitle="Votes Count"
                                    className='export-pdf-chart-wrapper'
                                    showLegend={true} 
                                    width={chartMaxWidth}
                                    height={chartLocationHeight} /> :
                                <StackedHorizontalBarChart
                                    key='votesByLocationChartMean'
                                    data={this.state.allLocationsShow ? votesCombinedByCityAndSorted : votesCombinedByCityAndSorted.slice(0, 10)}
                                    width={chartMaxWidth}
                                    height={chartLocationHeight}
                                    dataFieldName="city"
                                    stackFieldOptions={[STACKING_OPTIONS.sentiment]}
                                    aggregationFieldName="votesCount"
                                    chartYAxisTitle="Locations"
                                    chartXAxisTitle="Votes Count"
                                    className="export-pdf-chart-wrapper"
                                    forceResize={this.state.shouldForceResize}
                                    showLegend={true}
                                />}
                        </Card>

                        {votesCombinedByCityAndSorted && votesCombinedByCityAndSorted.length > 0 &&
                        <div className="btn-group">
                            {!this.state.allLocationsShow &&
                                <button type="button" className="btn btn-outline load-more" onClick={this.toggleShowAllLocations.bind(this)} disabled={(votesCombinedByCityAndSorted.length - 10) <= 0}>
                                    show more ({(votesCombinedByCityAndSorted.length - 10) > 0 ? votesCombinedByCityAndSorted.length - 10 : 0})
                                </button>
                            }

                            {this.state.allLocationsShow &&
                                <button type="button" className="btn btn-outline load-more" onClick={this.toggleShowAllLocations.bind(this)}>
                                    show less locations
                                </button>
                            }

                            <button type="button" className="btn btn-outline load-more" onClick={this.toggleShowVotesMap.bind(this)}>
                                {!this.state.votesMapShow ? "show" : "hide" } votes map
                            </button>
                        </div>
                        }
                    </div>
    
    
                    <div className="col-md-12 col-xs-12">
                        <FeaturesContext
                            requiredFeatures={[{
                                name: ENTITY_FEATURES.HAS_SOURCES_CHART,
                                compare: COMPARATORS.IS_TRUE
                            }]}
                            renderOtherwise={
                                <Card title="Votes by source" hoffset={false}>
                                    <NoPermissionLabel mode="panel" message={messages.permissions.features.noCustomSources} />
                                </Card>
                            }
                        >
                            <Card title="Votes by source" hoffset={false} className="pdf-export-votes-graph-wrapper">                                
                                <FeaturesContext
                                    hideIfNoPermission={true}
                                    requiredFeatures={[{
                                        name: ENTITY_FEATURES.HAS_SOURCES_CHART,
                                        compare: COMPARATORS.IS_TRUE
                                    }]}>
                                    
                                    <div className='chart-control-options export-pdf-chart-control-options'>
                                        <span>showing as&nbsp;</span>
                                        <Switch
                                            value={this.state.votesBySourceChartMode === VOTE_CHART_MODES.STACK}
                                            onChange={() => this.toggleVotesBy('votesBySourceChartMode')}
                                            offLabel="mean"
                                            onLabel="stack" />
                                    </div>
                                    {this.state.votesBySourceChartMode === VOTE_CHART_MODES.STACK ?
                                        <StackedHorizontalBarChart
                                            key='votesBySourceChartStack'
                                            data={votesWithSources}
                                            dataFieldName='sources'
                                            stackFieldOptions={[STACKING_OPTIONS.sentiment, STACKING_OPTIONS.gender, STACKING_OPTIONS.ageGroups]}
                                            chartYAxisTitle="Sources"
                                            chartYAxisLabelLimit={100}
                                            chartXAxisTitle="Votes Count"
                                            className='export-pdf-chart-wrapper'
                                            showLegend={true}
                                            width={chartMaxWidth}
                                            height={chartSourceHeight}
                                        /> 
                                        :
                                        <StackedHorizontalBarChart
                                            key='votesBySourceChartMean'
                                            data={votesCombinedBySourceAndSorted}
                                            width={chartMaxWidth}
                                            height={chartSourceHeight}
                                            dataFieldName="source"
                                            stackFieldOptions={[STACKING_OPTIONS.sentiment]}
                                            aggregationFieldName="votesCount"
                                            chartYAxisTitle="Sources"
                                            chartYAxisLabelLimit={100}
                                            chartXAxisTitle="Votes Count"
                                            className="export-pdf-chart-wrapper"
                                            forceResize={this.state.shouldForceResize}
                                            showLegend={true}
                                        />}
                                        
                                </FeaturesContext>
                                
                            </Card>
                        </FeaturesContext>
                    </div>
                </div>
            </div>
        );
    }
}

TopicDetailsGraphs.propTypes = {
    votes: PropTypes.array,
    questionnaire: PropTypes.array,
    questionnaireTotal: PropTypes.number,
    dateFrom: PropTypes.any,
    dateTo: PropTypes.any,
    periodIndex: PropTypes.number,
    voterFilters: PropTypes.object,
};

export default TopicDetailsGraphs;
